import React, { useEffect, createElement } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
declare global {
  interface Window {
    Tally?: {
      loadEmbeds: () => void;
      openPopup: (formId: string, options?: Record<string, any>) => void;
      closePopup: (formId: string) => void;
    };
  }
}
const TALLY_FORM_ID = '2E4rjD';
export function HeroSection() {
  useEffect(() => {
    const TALLY_SRC = 'https://tally.so/widgets/embed.js';
    if (window.Tally) return;
    if (document.querySelector(`script[src="${TALLY_SRC}"]`)) return;
    const script = document.createElement('script');
    script.src = TALLY_SRC;
    script.async = true;
    document.head.appendChild(script);
  }, []);
  const openRegisterPopup = () => {
    const open = () => {
      if (window.Tally) {
        window.Tally.openPopup(TALLY_FORM_ID, {
          layout: 'modal',
          width: 700,
          autoClose: 5000
        });
      } else {
        window.open(`https://tally.so/r/${TALLY_FORM_ID}`, '_blank');
      }
    };
    if (window.Tally) {
      open();
    } else {
      setTimeout(open, 500);
    }
  };
  return <section className="w-full bg-white" data-id="element-296">
      <div className="max-w-7xl mx-auto px-5 md:px-12 pt-8 pb-10 md:pt-16 md:pb-20" data-id="element-297">
        <div className="flex flex-col items-center text-center max-w-2xl mx-auto" data-id="element-298">
          <h1 className="mb-4 md:mb-5 text-[2rem] md:text-[3.25rem] font-semibold text-[#111111] leading-[1.15] tracking-tight" data-id="element-299">
            <div className="text-[40px]" data-id="element-300">
              Most families know they should do more to stay safe online.
            </div>
            <div className="text-[#631bb7] text-[40px]" data-id="element-301">
              Caveo makes sure they actually do it.
            </div>
          </h1>

          <p className="md:text-lg text-gray-500 mb-6 md:mb-8 max-w-xl mx-auto text-[16px] font-[400]" data-id="element-302">
            Knowing isn't enough. Caveo guides every member of your family
            through the actions that matter and keeps them on track so nothing
            slips.
          </p>

          <div className="flex flex-row items-center gap-4 mb-6 md:mb-8" data-id="element-303">
            <Link to="/onboarding" className="bg-[#1a0647] text-white px-7 py-3.5 rounded-md font-medium text-sm no-underline hover:bg-[#2a0a6e] transition-colors" data-id="element-304">
              Get Started
            </Link>
            <a href="#how-it-works" className="text-[#111111] font-medium text-sm no-underline hover:text-gray-600 transition-colors flex items-center gap-1.5 py-3.5" data-id="element-305">
              <span data-id="element-306">See How Caveo Works</span>
              <ChevronRightIcon size={16} data-id="element-307" />
            </a>
          </div>

          {/* Social proof avatars */}
          <div className="flex items-center gap-2.5 flex-wrap justify-center" data-id="element-308">
            <div className="flex -space-x-2" data-id="element-309">
              <img src="https://cdn.magicpatterns.com/uploads/6giMtsG68yxp89aaHTtkX6/Screenshot_2026-02-22_at_12.56.48_AM.png" alt="Member" className="w-7 h-7 rounded-full object-cover border-2 border-white" data-id="element-310" />
              <img src="https://cdn.magicpatterns.com/uploads/iaPLUAtSe7j7WSJaQUSv1g/Screenshot_2026-02-22_at_12.56.42_AM.png" alt="Member" className="w-7 h-7 rounded-full object-cover border-2 border-white" data-id="element-311" />
              <img src="https://cdn.magicpatterns.com/uploads/hCu7TZEUwwQyaw75WuLKPE/Screenshot_2026-02-22_at_12.56.35_AM.png" alt="Member" className="w-7 h-7 rounded-full object-cover border-2 border-white" data-id="element-312" />
              <img src="https://cdn.magicpatterns.com/uploads/wJ71gACjNWRRTKd8byxEK6/Screenshot_2026-02-22_at_12.51.11_AM.png" alt="Member" className="w-7 h-7 rounded-full object-cover border-2 border-white" data-id="element-313" />
              <img src="https://cdn.magicpatterns.com/uploads/f2JgivSXHBCeH7uM5GKP7x/Screenshot_2026-02-22_at_12.55.40_AM.png" alt="Member" className="w-7 h-7 rounded-full object-cover border-2 border-white" data-id="element-314" />
            </div>
            <p className="text-xs text-gray-500 font-medium" data-id="element-315">
              Helping families actually get protected — not just informed
            </p>
          </div>
        </div>

        {/* Logo strip */}
        <div className="pt-16 md:pt-24 pb-4" data-id="element-316">
          <p className="text-center text-sm text-gray-500 mb-8 font-medium" data-id="element-317">
            Trusted by families who take digital safety seriously
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10" data-id="element-318">
            <img src="https://cdn.magicpatterns.com/uploads/ut1pKKvicHWLWvo6NRe2GY/transparent-wsj-logo-png-the-wall-street-journal-c-8c851bcb8d9e4624.jpg" alt="Wall Street Journal" className="h-4 md:h-5 opacity-40 grayscale" data-id="element-319" />
            <img src="https://cdn.magicpatterns.com/uploads/oZnCNixd9RzyC1qjqpbC9m/The_New_York_Times_logo.png" alt="The New York Times" className="h-3 md:h-4 opacity-40 grayscale" data-id="element-320" />
            <img src="https://cdn.magicpatterns.com/uploads/xty7mcBNjyknELbhKf8VgC/forbes.png" alt="Forbes" className="h-4 md:h-5 opacity-40 grayscale" data-id="element-321" />
            <img src="https://cdn.magicpatterns.com/uploads/54LBYQ2axiem9cZcDJAjjG/Bloomberg_Logo.png" alt="Bloomberg" className="h-3 md:h-4 opacity-40 grayscale" data-id="element-322" />
            <img src="https://cdn.magicpatterns.com/uploads/2aPSjLGvnBG7VdpaAjHKcr/wired-logo-transparent.png" alt="Wired" className="h-3 md:h-4 opacity-40 grayscale" data-id="element-323" />
            <img src="https://cdn.magicpatterns.com/uploads/wG62U3uK5ijYVDcfEJtPYN/cnbc.png" alt="CNBC" className="h-3 md:h-4 opacity-40 grayscale" data-id="element-324" />
          </div>
        </div>

        {/* Register CTA */}
        <div className="flex justify-center pt-10 md:pt-12" data-id="element-325">
          <button type="button" onClick={openRegisterPopup} className="bg-[#1a0647] text-white px-8 py-3.5 rounded-md font-medium text-sm hover:bg-[#2a0a6e] transition-colors" data-id="element-326">
            Register
          </button>
        </div>
      </div>
    </section>;
}